// src/features/hmi-simulator/components/screens/SettingsScreen.tsx
import React from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  Divider,
  IconButton
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Header } from '../shared/Header';
import { HMIConfig } from '../../types';

interface SettingsScreenProps {
  config: HMIConfig;
  language: string;
  setLanguage: (lang: string) => void;
  isOnline: boolean;
  api: any;
  setScreen: (screen: any) => void;
  controllerConfig: any;
  controllerStatuses: Map<string, boolean>;
  deviceId?: string;
  onResetDevice: () => void;
}

export const SettingsScreen: React.FC<SettingsScreenProps> = ({
  config,
  language,
  setLanguage,
  isOnline,
  api,
  setScreen,
  controllerConfig,
  controllerStatuses,
  deviceId,
  onResetDevice
}) => {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <Header
        config={config}
        language={language}
        setLanguage={setLanguage}
        isOnline={isOnline}
        deviceId={deviceId}
        onSettingsClick={() => setScreen('main')}
      />
      
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h5">Device Settings</Typography>
              <IconButton onClick={() => setScreen('main')}>
                <Close />
              </IconButton>
            </Box>
            
            <Stack spacing={3}>
              {/* Device Information */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Device ID
                </Typography>
                <Typography variant="body1" fontFamily="monospace">
                  {deviceId || 'Not provisioned'}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Hotel
                </Typography>
                <Typography variant="body1">
                  {config.hotelName}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Primary Color
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: 1,
                      backgroundColor: config.primaryColor,
                      border: '1px solid #ccc'
                    }}
                  />
                  <Typography variant="body2" fontFamily="monospace">
                    {config.primaryColor}
                  </Typography>
                </Box>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  HMI Status
                </Typography>
                <Chip 
                  label={isOnline ? 'Online' : 'Offline'} 
                  color={isOnline ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              
              {/* Controller Configuration */}
              {controllerConfig && (
                <>
                  <Divider />
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      HMI Controller
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {controllerConfig.hmiControllerIp}:{controllerConfig.tcpPort}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Timeout: {controllerConfig.connectionTimeout}ms | 
                      Retries: {controllerConfig.retryAttempts}
                    </Typography>
                  </Box>
                  
                  {controllerConfig.lockerControllers && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Locker Controllers ({controllerConfig.lockerControllers.length})
                      </Typography>
                      <Stack spacing={1}>
                        {controllerConfig.lockerControllers.map((controller: any) => {
                          const key = `${controller.ipAddress}:${controller.tcpPort}`;
                          const isControllerOnline = controllerStatuses.get(key);
                          return (
                            <Box 
                              key={controller.id} 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                p: 1,
                                bgcolor: 'grey.50',
                                borderRadius: 1
                              }}
                            >
                              <Chip
                                size="small"
                                label={isControllerOnline ? 'Online' : 'Offline'}
                                color={isControllerOnline ? 'success' : 'default'}
                              />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight="medium">
                                  {controller.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                                  {controller.ipAddress}:{controller.tcpPort}
                                </Typography>
                                {controller.zoneName && (
                                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    Zone: {controller.zoneName}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}
                </>
              )}
              <Divider />
              
              {/* Actions */}
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={onResetDevice}
                  fullWidth
                >
                  Reset Device (Deprovision)
                </Button>
                
                <Typography variant="caption" color="text.secondary" align="center">
                  Warning: Resetting will remove all configuration and require reprovisioning
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};